/**
 * Sanitizes user input to prevent prompt injection attacks
 * Strips control characters, limits length, and escapes template injection
 */

/**
 * Strip control characters (0x00-0x1F, 0x7F)
 * 
 * @param str - Input string to clean
 * @returns String with control characters removed
 * 
 * @example
 * stripControlChars('Hello\x00World\x1F')
 * // Returns: 'HelloWorld'
 */
function stripControlChars(str: string): string {
  return str.replace(/[\x00-\x1F\x7F]/g, '');
}

/**
 * Escape template literals to prevent injection
 * Prevents template literal injection by escaping backticks, $, and backslashes
 * 
 * @param str - Input string to escape
 * @returns Escaped string safe for template literals
 * 
 * @example
 * escapeTemplateLiterals('Hello ${user.name}')
 * // Returns: 'Hello \${user.name}'
 * 
 * @example
 * escapeTemplateLiterals('Backtick: `code`')
 * // Returns: 'Backtick: \`code\`'
 */
function escapeTemplateLiterals(str: string): string {
  return str
    .replace(/\\/g, '\\\\')  // Escape backslashes first
    .replace(/\$/g, '\\$')   // Escape $ to prevent ${} injection
    .replace(/`/g, '\\`');   // Escape backticks
}

/**
 * Sanitize string input to prevent security issues and normalize data
 * Strips control characters, escapes template literals, trims whitespace, and enforces length limits
 * 
 * @param input - String input to sanitize
 * @param options - Sanitization options
 * @param options.maxLength - Maximum allowed length (truncates if longer)
 * @param options.stripControls - Whether to strip control characters (default: true)
 * @param options.escapeTemplates - Whether to escape template literals (default: true)
 * @returns Sanitized string
 * 
 * @example
 * sanitizeInput('Hello\x00World')
 * // Returns: 'HelloWorld'
 * 
 * @example
 * sanitizeInput('  Hello ${injection}  ', { maxLength: 10 })
 * // Returns: 'Hello \${injection}'
 * 
 * @example
 * sanitizeInput('user input', { stripControls: false, escapeTemplates: false })
 * // Returns: 'user input'
 */
export function sanitizeInput(
  input: string,
  options: {
    maxLength?: number;
    stripControls?: boolean;
    escapeTemplates?: boolean;
  } = {}
): string {
  if (typeof input !== 'string') {
    return '';
  }

  let sanitized = input;

  // Strip control characters by default
  if (options.stripControls !== false) {
    sanitized = stripControlChars(sanitized);
  }

  // Escape template literals by default
  if (options.escapeTemplates !== false) {
    sanitized = escapeTemplateLiterals(sanitized);
  }

  // Trim whitespace
  sanitized = sanitized.trim();

  // Limit length if specified
  if (options.maxLength && sanitized.length > options.maxLength) {
    sanitized = sanitized.substring(0, options.maxLength);
  }

  return sanitized;
}

/**
 * Sanitize multiple string inputs at once with field-specific options
 * Useful for form data where different fields have different validation rules
 * 
 * @param inputs - Object with string values to sanitize
 * @param fieldOptions - Per-field sanitization options (keyed by field name)
 * @returns Object with sanitized values
 * 
 * @example
 * sanitizeInputs(
 *   { name: 'John Doe', bio: 'Hello\x00World', email: '  john@example.com  ' },
 *   { bio: { maxLength: 500 }, email: { maxLength: 100 } }
 * )
 * // Returns: { name: 'John Doe', bio: 'HelloWorld', email: 'john@example.com' }
 */
export function sanitizeInputs(
  inputs: Record<string, string>,
  fieldOptions: Record<string, { maxLength?: number }> = {}
): Record<string, string> {
  const sanitized: Record<string, string> = {};

  for (const [key, value] of Object.entries(inputs)) {
    const options = fieldOptions[key] || {};
    sanitized[key] = sanitizeInput(value, options);
  }

  return sanitized;
}